import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import Dropdown from "../../ui/SelectDropdown";
import ConfirmDispatch from "./ConfirmDispatch";
import { getStatusesAfter, mapDataToLabelValue } from "../../../helpers";
import { RootState } from "../../../redux/app/store";
import { fetchCourierOffice } from "../../../redux/features/CourrierSlice";

// Define form data interface
interface EditWeightFormData {
  courierOfficeId: string | undefined;
  dispatchStatus: string;
  actualCourierFee: number;
}

// Define props
interface UpdateDispatchStatusProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: any;
  order: any;
  courierId?: string;
}

// Validation schema with Yup
const validationSchema = Yup.object({
  dispatchStatus: Yup.string().required("Dispatch status is required"),
});

const UpdateDispatchStatus: React.FC<UpdateDispatchStatusProps> = ({
  isOpen,
  onClose,
  order,
  onSave,
  courierId,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [showComfirmDispatch, setShowConfirmDispatch] = useState(false);
  const dispatch = useAppDispatch();

  const { courierOffices } = useAppSelector(
    (state: RootState) => state.courier
  );
  const [formData, setFormData] = useState<EditWeightFormData>({
    courierOfficeId: user?.user?.adminCourierOfficeId || courierId,
    dispatchStatus: "",
    actualCourierFee: 0,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof EditWeightFormData, string>>
  >({});

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: Number(value),
    });
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors: any) {
      const validationResult: Partial<
        Record<keyof EditWeightFormData, string>
      > = {};
      validationErrors.inner.forEach((err: any) => {
        validationResult[err.path as keyof EditWeightFormData] = err.message;
      });
      setErrors(validationResult);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    if (isValid) {
      setShowConfirmDispatch(true);
    }
  };

  const handleDiscard = () => {
    setFormData({
      courierOfficeId: user?.user?.adminCourierOfficeId ?? "",
      dispatchStatus: "",
      actualCourierFee: 0,
    });
    onClose();
  };

  useEffect(() => {
    dispatch(fetchCourierOffice({}));
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <CustomModalContainer
      title="Edit Order Dispatch Status"
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            onClick={handleDiscard}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",  
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Save Changes"
            type="button"
            onClick={handleSave}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
          />
        </div>
      }
      open={isOpen}
      onClose={handleDiscard}
    >
      {/* Modal Body */}
      <div className="p-6">
        <form>
          {/* Courier Office ID */}
          <ModalInput
            label="Name of Item"
            value={order?.contract?.title}
            required
            disabled
          />

          {/* Order ID */}
          <ModalInput
            label="Contract Code"
            value={order?.contract?.contractCode}
            required
            disabled
          />

          {/* Dispatch Status */}
          <Dropdown
            label="Dispatch Status"
            options={getStatusesAfter(order?.dispatchStatus)}
            value={formData.dispatchStatus}
            onSelect={(value) => handleSelectChange("dispatchStatus", value)}
            error={errors.dispatchStatus}
            placeholder="Select dispatch status"
            required
          />

          {user?.user?.roles === "SUPER_ADMIN" && (
            <Dropdown
              options={mapDataToLabelValue(
                courierOffices?.data?.data,
                "name",
                "id"
              )}
              onSelect={(value) => handleSelectChange("courierOfficeId", value)}
              placeholder="Select Office"
            />
          )}
          {formData?.dispatchStatus === "Dispatch" &&
            order?.orderType !== "RETURN" && (
              <>
                <ModalInput
                  label="Estimated Courier Fee"
                  name="estimatedCourierFee"
                  placeholder="Enter estimated weight"
                  value={order?.estimatedCourierFee}
                  type="number"
                  required
                  disabled
                />
                <ModalInput
                  label="Actual Courier Fee"
                  name="actualCourierFee"
                  placeholder="Enter actual weight"
                  value={formData.actualCourierFee.toString()}
                  onChange={handleInputChange}
                  type="number"
                  required
                />
              </>
            )}
        </form>
      </div>
      <ConfirmDispatch
        isOpen={showComfirmDispatch}
        dispatchDetails={{
          courierOfficeId: formData.courierOfficeId,
          orderId: order?.id,
          dispatchStatus: formData?.dispatchStatus,
          actualCourierFee: formData?.actualCourierFee,
        }}
        onClose={() => {
          setShowConfirmDispatch(false);
          handleDiscard();
        }}
        onSave={() => {
          onSave();
          handleDiscard();
        }}
        contractId={order?.contractId}
      />
    </CustomModalContainer>
  );
};

export default UpdateDispatchStatus;
